/**
 * @file MoveTableModal.tsx
 * @description Dialog allowing servers to relocate open table orders to another available table.
 */

import React, { useState } from 'react';
import { X, ArrowRightLeft } from 'lucide-react';
import type { WaiterTable } from '../../../types/waiter.types';

interface MoveTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceTable: WaiterTable;
  availableTables: WaiterTable[];
  onConfirmMove: (destinationTableId: string, destinationTableNumber: string) => Promise<void>;
}

export const MoveTableModal: React.FC<MoveTableModalProps> = ({
  isOpen,
  onClose,
  sourceTable,
  availableTables,
  onConfirmMove,
}) => {
  const [selectedDestId, setSelectedDestId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const validDestinations = availableTables.filter(t => t.id !== sourceTable.id);

  const handleMove = async () => {
    const dest = validDestinations.find(t => t.id === selectedDestId);
    if (!dest) return;

    try {
      setIsSubmitting(true);
      await onConfirmMove(dest.id, dest.table_number);
      onClose();
    } catch (err) {
      console.error('Failed to move table:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl flex flex-col max-h-[85dvh] pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:pb-6">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center -mt-2 pb-3 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2 text-amber-400">
            <ArrowRightLeft className="w-5 h-5 shrink-0" />
            <h3 className="text-base font-black uppercase text-white">Move Table</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-zinc-400 mb-4 shrink-0">
          Moving active bill from <span className="font-bold text-white">TABLE {sourceTable.table_number}</span> to a new table.
        </p>

        <div className="overflow-y-auto space-y-2 mb-6 flex-1 pr-1">
          {validDestinations.length === 0 ? (
            <p className="text-xs text-zinc-500 text-center py-6">No other tables available.</p>
          ) : (
            validDestinations.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedDestId(t.id)}
                className={`w-full p-3.5 min-h-[44px] rounded-xl text-xs font-bold text-left flex items-center justify-between border transition-all active:scale-[0.98] ${
                  selectedDestId === t.id
                    ? 'bg-amber-500/10 text-amber-300 border-amber-500'
                    : 'bg-zinc-900 text-zinc-300 border-zinc-800'
                }`}
              >
                <span>TABLE {t.table_number}</span>
                <span className="text-zinc-500 text-[11px] font-normal">{t.section} • {t.seats} seats</span>
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3.5 min-h-[44px] rounded-xl bg-zinc-900 text-zinc-400 text-xs font-bold hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedDestId || isSubmitting}
            onClick={handleMove}
            className="flex-1 py-3.5 min-h-[44px] rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase tracking-wider disabled:opacity-50 active:scale-[0.98] transition-all"
          >
            {isSubmitting ? 'Relocating...' : 'Move Order'}
          </button>
        </div>
      </div>
    </div>
  );
};
